import React, { useState } from "react";

import { Spinner, Text } from "@performyard/py-components";

import AccordionSectionHeader from "./AccordionSectionHeader";

import { config } from "../config";

type ChecklistSectionProps = {
	title: string;
	isLoading: boolean;
	isCollapsible?: boolean;
	defaultExpanded?: boolean;
	hasError?: boolean;
	errorMessage?: string;
	children: React.ReactNode;
	footer?: React.ReactNode;
	variant?: "sidebar" | "main";
};

/**
 * Reusable section wrapper for checklist-style content (agenda items, action items).
 * Provides consistent styling, loading states, and optional collapsible behavior.
 * @param title - Section header title
 * @param isLoading - Whether content is loading
 * @param isCollapsible - Whether section can be collapsed
 * @param defaultExpanded - Initial expanded state for collapsible sections
 * @param hasError - Whether there's an error state
 * @param errorMessage - Error message to display
 * @param children - Section content
 * @param footer - Footer content (e.g., add button)
 * @param variant - Visual variant: 'sidebar' has sticky header, 'main' is simpler
 */
const ChecklistSection = ({
	title,
	isLoading,
	isCollapsible = false,
	defaultExpanded = true,
	hasError = false,
	errorMessage = "Failed to load items",
	children,
	footer,
	variant = "main",
}: ChecklistSectionProps): React.ReactElement => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const isSidebarVariant = variant === "sidebar";
	const showContent = !isCollapsible || isExpanded;

	const handleToggle = (): void => {
		if (isCollapsible) {
			setIsExpanded(!isExpanded);
		}
	};

	const renderHeader = (): React.ReactElement => {
		if (isSidebarVariant) {
			return (
				<AccordionSectionHeader
					title={title}
					isExpanded={isExpanded}
					onToggle={handleToggle}
					isCollapsible={isCollapsible}
				/>
			);
		}

		return (
			<Text size="paragraph-2" weight="semi-bold">
				{title}
			</Text>
		);
	};

	const renderContent = (): React.ReactElement => {
		if (isLoading) {
			return (
				<div className="flex items-center justify-center py-8">
					<Spinner size={config.spinnerSize.medium} />
				</div>
			);
		}

		if (hasError) {
			return (
				<div className="flex items-center justify-center py-8">
					<Text size="paragraph-3" weight="regular" color="red-5">
						{errorMessage}
					</Text>
				</div>
			);
		}

		return <>{children}</>;
	};

	if (isSidebarVariant) {
		return (
			<div className="flex flex-col border-b border-solid border-neutral-2 bg-white">
				{renderHeader()}
				{showContent && (
					<div
						className="flex flex-col gap-4 overflow-y-auto bg-white p-4"
						style={{ maxHeight: config.layout.accordionMaxHeight }}
					>
						<div className="flex flex-col gap-2">{renderContent()}</div>
						{footer && !isLoading && !hasError && footer}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4 rounded-lg">
			<div className="flex flex-col gap-2">
				{renderHeader()}
				{renderContent()}
			</div>
			{footer && !isLoading && !hasError && footer}
		</div>
	);
};

export default ChecklistSection;
